import { State } from "../state/state";
import {
  BrickInventoryEntry,
  loadBrickInventory,
} from "../state/BrickInventory/BrickInventory";
import {
  ActionEntry,
  loadActionEntries,
} from "../state/BrickActions/ActionEntries";
import {
  BrickPackPurchase,
  loadBrickPackPurchaseHistory,
  loadBrickPackStats,
  loadBrickPackUpgrades,
} from "../state/BrickPackHistory/BrickPackPurchase";
import { SetEntry, SetInventory } from "../state/SetInventory/SetInventory";
import { AchievementInfoTotal } from "../state/Achievements/AchievementInfoTotal";

const SAVE_KEY = "brickpacked_save";
const SAVE_VERSION = "1.2.0";

/**
 * Serializable save data format
 * Only includes player progress, not metadata
 * Undiscovered bricks (amount = NaN) are excluded entirely from the save
 * Non-visible actions (visible = false) are excluded entirely from the save
 * Brick packs with no purchases (stats = 0 or history = []) are excluded entirely from the save
 * Sets that are not visible AND have count = 0 are excluded entirely from the save
 * Set costScale is not saved (always 0, no scaling)
 * Edit mode flags are not saved (always default to false on load)
 */
export interface SaveData {
  version: string;
  timestamp: number;

  // Brick inventory data (only runtime values for discovered bricks)
  bricks: {
    [brickId: string]: {
      amount: number; // Only discovered bricks are saved
      capacity: number;
      autoCollect: number;
      userHidden: boolean;
    };
  };

  // Action data (only runtime values)
  actions: {
    [actionId: string]: {
      visible: boolean;
      costScale: number;
      outcomeScale: number;
      userHidden: boolean;
    };
  };

  // Brick pack data
  // Note: brickContents is stored as plain object in JSON (Maps don't serialize)
  brickPackHistory: {
    [packId: string]: Array<{
      timestamp: string;
      totalPacks: number;
      brickContents: { [brickId: string]: number };
    }>;
  };
  brickPackStats: {
    [packId: string]: number;
  };
  brickPackUpgrades: {
    [packId: string]: number;
  };
  brickPackAutoCollect: {
    [packId: string]: number;
  };

  // Set data (only sets with count > 0 OR visible === true)
  // costScale is not saved (always 0, no scaling)
  sets: {
    [setId: string]: {
      count: number;
      visible: boolean;
      userHidden: boolean;
    };
  };

  // Player settings
  useActionRows: boolean;
  useInventoryRows: boolean;
  actionOrder: string[]; // Custom action order (array of action IDs)
  inventoryOrder: string[]; // Custom inventory order (array of brick IDs)
  helpIconAnimated: boolean; // Whether help icon animation is enabled
  // Note: editModeActive and editModeInventory are not saved (always default to false)

  // Buy amount selections (brickPackId/setId -> selected amount)
  brickPackBuyAmounts: { [packId: string]: number };
  setBuyAmounts: { [setId: string]: number };

  // Achievement tracking stats
  totalBricksCollected: number;
  bricksCollectedByType: { [brickId: string]: number };
  totalActionsTriggered: number;
  actionsTriggeredByType: { [actionId: string]: number };
  totalSetsPurchased: number;
  setsPurchasedByType: { [setId: string]: number };

  // Unlocked achievements (stored as array for JSON serialization)
  unlockedAchievements: string[];
}

/**
 * Serializes the current game state into a JSON-friendly format
 * Only saves discovered bricks (amount is not NaN) to reduce file size
 * Only saves visible actions (visible is true) to reduce file size
 * Only saves brick packs with purchases (stats > 0 or history.length > 0) to reduce file size
 * Only saves sets with count > 0 OR visible === true to reduce file size
 * Set costScale is not saved (always 0, no scaling)
 * Edit mode flags are not saved (always default to false on load)
 */
export const serializeState = (state: State): SaveData => {
  // Convert bricks Map to object, but only include discovered bricks
  const bricks: SaveData["bricks"] = {};
  state.bricks.forEach((brick, brickId) => {
    // Skip bricks that haven't been discovered (amount is NaN)
    if (!isNaN(brick.amount)) {
      bricks[brickId] = {
        amount: brick.amount,
        capacity: brick.capacity,
        autoCollect: brick.autoCollect,
        userHidden: brick.userHidden,
      };
      // Debug logging for userHidden
      if (brick.userHidden) {
      }
    }
  });

  // Convert actions Map to object (only include visible actions)
  const actions: SaveData["actions"] = {};
  state.actions.forEach((action, actionId) => {
    // Skip actions that haven't been unlocked (visible is false)
    if (action.visible) {
      actions[actionId] = {
        visible: action.visible,
        costScale: action.costScale,
        outcomeScale: action.outcomeScale,
        userHidden: action.userHidden,
      };
    }
  });

  // Convert brick pack history Map to object
  // Also convert each purchase's brickContents Map to plain object
  // Only include brick packs with non-empty history arrays to reduce file size
  const brickPackHistory: SaveData["brickPackHistory"] = {};
  state.brickPackHistory.forEach((history, packId) => {
    // Skip brick packs with empty history arrays
    if (history.length > 0) {
      brickPackHistory[packId] = history.map((purchase) => ({
        timestamp: purchase.timestamp,
        totalPacks: purchase.totalPacks,
        brickContents: Object.fromEntries(purchase.brickContents), // Map → Object
      }));
    }
  });

  // Convert brick pack stats Map to object
  // Only include brick packs with purchase amount > 0 to reduce file size
  const brickPackStats: SaveData["brickPackStats"] = {};
  state.brickPackStats.forEach((stat, packId) => {
    // Skip brick packs with 0 purchases
    if (stat > 0) {
      brickPackStats[packId] = stat;
    }
  });

  // Convert brick pack upgrades Map to object
  // Only include brick packs with upgrade count > 0 to reduce file size
  const brickPackUpgrades: SaveData["brickPackUpgrades"] = {};
  state.brickPackUpgrades.forEach((upgradeCount, packId) => {
    // Skip brick packs with 0 upgrades
    if (upgradeCount > 0) {
      brickPackUpgrades[packId] = upgradeCount;
    }
  });

  // Convert brick pack auto-collect Map to object
  // Only include brick packs with auto-collect > 0 to reduce file size
  const brickPackAutoCollect: SaveData["brickPackAutoCollect"] = {};
  state.brickPackAutoCollect.forEach((purchasesPerSecond, packId) => {
    // Skip brick packs with 0 auto-collect
    if (purchasesPerSecond > 0) {
      brickPackAutoCollect[packId] = purchasesPerSecond;
    }
  });

  // Convert sets Map to object
  // Only include sets with count > 0 OR visible === true to reduce file size
  // costScale is not saved (always 0, no scaling)
  const sets: SaveData["sets"] = {};
  state.sets.forEach((set, setId) => {
    // Skip sets with 0 count AND not visible (only save revealed sets or owned sets)
    if (set.count > 0 || set.visible) {
      sets[setId] = {
        count: set.count,
        visible: set.visible,
        userHidden: set.userHidden,
      };
    }
  });

  // Convert achievement tracking Maps to objects
  const bricksCollectedByType: { [brickId: string]: number } = {};
  state.bricksCollectedByType.forEach((count, brickId) => {
    if (count > 0) {
      bricksCollectedByType[brickId] = count;
    }
  });

  const actionsTriggeredByType: { [actionId: string]: number } = {};
  state.actionsTriggeredByType.forEach((count, actionId) => {
    if (count > 0) {
      actionsTriggeredByType[actionId] = count;
    }
  });

  const setsPurchasedByType: { [setId: string]: number } = {};
  state.setsPurchasedByType.forEach((count, setId) => {
    if (count > 0) {
      setsPurchasedByType[setId] = count;
    }
  });

  // Convert unlocked achievements Set to array
  const unlockedAchievements = Array.from(state.unlockedAchievements);

  // Convert buy amounts Maps to objects
  const brickPackBuyAmounts: { [packId: string]: number } = {};
  state.brickPackBuyAmounts.forEach((amount, packId) => {
    brickPackBuyAmounts[packId] = amount;
  });

  const setBuyAmounts: { [setId: string]: number } = {};
  state.setBuyAmounts.forEach((amount, setId) => {
    setBuyAmounts[setId] = amount;
  });

  return {
    version: SAVE_VERSION,
    timestamp: Date.now(),
    bricks,
    actions,
    brickPackHistory,
    brickPackStats,
    brickPackUpgrades,
    brickPackAutoCollect,
    sets,
    useActionRows: state.useActionRows,
    useInventoryRows: state.useInventoryRows,
    actionOrder: state.actionOrder,
    inventoryOrder: state.inventoryOrder,
    helpIconAnimated: state.helpIconAnimated,
    // Note: editModeActive and editModeInventory are not saved (always default to false)
    brickPackBuyAmounts,
    setBuyAmounts,
    totalBricksCollected: state.totalBricksCollected,
    bricksCollectedByType,
    totalActionsTriggered: state.totalActionsTriggered,
    actionsTriggeredByType,
    totalSetsPurchased: state.totalSetsPurchased,
    setsPurchasedByType,
    unlockedAchievements,
  };
};

/**
 * Deserializes save data and merges it with the base state
 * Handles missing properties gracefully by using defaults
 * Bricks not in save data default to: amount=NaN, capacity=30, autoCollect=0, userHidden=false
 * Actions not in save data default to: visible=false, costScale=0, outcomeScale=1, userHidden=false
 * Brick packs not in save data default to: stats=0, history=[], upgrades=0
 * Sets not in save data default to: count=0, costScale=0, visible=false, userHidden=false
 * Edit mode flags always default to: editModeActive=false, editModeInventory=false
 */
export const deserializeState = (
  saveData: SaveData,
  baseState: State
): State => {
  // Start with fresh base state (has all metadata and defaults)
  // All bricks default to: amount=NaN, capacity=30, autoCollect=0
  // All actions default to: visible=false, costScale=0, outcomeScale=1
  const newBricks = new Map<string, BrickInventoryEntry>(loadBrickInventory());
  const newActions = new Map<string, ActionEntry>(loadActionEntries());
  const newBrickPackHistory = new Map<string, BrickPackPurchase[]>(
    loadBrickPackPurchaseHistory()
  );
  const newBrickPackStats = new Map<string, number>(loadBrickPackStats());
  const newBrickPackUpgrades = new Map<string, number>(loadBrickPackUpgrades());
  const newSets = new Map<string, SetEntry>(SetInventory);

  // Merge brick data from save (only discovered bricks are in save data)
  Object.entries(saveData.bricks).forEach(([brickId, brickData]) => {
    const existingBrick = newBricks.get(brickId);
    if (existingBrick) {
      // Create a NEW object with updated values instead of mutating
      newBricks.set(brickId, {
        ...existingBrick,
        amount: brickData.amount,
        capacity: brickData.capacity,
        autoCollect: brickData.autoCollect,
        userHidden: brickData.userHidden ?? false, // Default to false for old saves
      });
    }
  });

  // Merge action data from save (only visible actions are in save data)
  // Missing actions retain defaults: visible=false, costScale=0, outcomeScale=1, userHidden=false
  Object.entries(saveData.actions).forEach(([actionId, actionData]) => {
    const existingAction = newActions.get(actionId);
    if (existingAction) {
      existingAction.visible = actionData.visible;
      // IMPORTANT: Brick packs should ALWAYS have costScale = 0 (flat costs, no scaling)
      // This ensures old save files with non-zero costScale get reset to 0
      existingAction.costScale = existingAction.isBrickpack
        ? 0
        : actionData.costScale;
      existingAction.outcomeScale = actionData.outcomeScale;
      existingAction.userHidden = actionData.userHidden ?? false; // Default to false for old saves
      newActions.set(actionId, existingAction);
    }
  });

  // Merge brick pack history from save
  // Convert each purchase's brickContents plain object back to Map
  Object.entries(saveData.brickPackHistory).forEach(([packId, history]) => {
    const convertedHistory = history.map((purchase) => ({
      timestamp: purchase.timestamp,
      totalPacks: purchase.totalPacks,
      // Handle both object and empty object cases
      brickContents: new Map(
        purchase.brickContents ? Object.entries(purchase.brickContents) : []
      ), // Object → Map
    }));
    newBrickPackHistory.set(packId, convertedHistory);
  });

  // Merge brick pack stats from save
  Object.entries(saveData.brickPackStats).forEach(([packId, stat]) => {
    newBrickPackStats.set(packId, stat);
  });

  // Merge brick pack upgrades from save (with fallback for old saves)
  if (saveData.brickPackUpgrades) {
    Object.entries(saveData.brickPackUpgrades).forEach(
      ([packId, upgradeCount]) => {
        newBrickPackUpgrades.set(packId, upgradeCount);
      }
    );
  }

  // Merge brick pack auto-collect from save (with fallback for old saves)
  const newBrickPackAutoCollect = new Map<string, number>();
  if (saveData.brickPackAutoCollect) {
    Object.entries(saveData.brickPackAutoCollect).forEach(
      ([packId, purchasesPerSecond]) => {
        newBrickPackAutoCollect.set(packId, purchasesPerSecond);
      }
    );
  }

  // Merge sets from save (with fallback for old saves without sets)
  // Sets not in save data default to: count=0, costScale=0, visible=false, userHidden=false
  // costScale is always 0 for sets (no scaling)
  if (saveData.sets) {
    Object.entries(saveData.sets).forEach(([setId, setData]) => {
      const existingSet = newSets.get(setId);
      if (existingSet) {
        // Create a NEW object with updated values instead of mutating
        newSets.set(setId, {
          ...existingSet,
          count: setData.count,
          costScale: 0, // Always 0 for sets (no scaling)
          visible: setData.visible,
          userHidden: setData.userHidden ?? false, // Default to false for old saves
        });
      }
    });
  }

  // Merge achievement tracking stats from save (with fallback for old saves)
  const newBricksCollectedByType = new Map<string, number>();
  if (saveData.bricksCollectedByType) {
    Object.entries(saveData.bricksCollectedByType).forEach(
      ([brickId, count]) => {
        newBricksCollectedByType.set(brickId, count);
      }
    );
  }

  const newActionsTriggeredByType = new Map<string, number>();
  if (saveData.actionsTriggeredByType) {
    Object.entries(saveData.actionsTriggeredByType).forEach(
      ([actionId, count]) => {
        newActionsTriggeredByType.set(actionId, count);
      }
    );
  }

  const newSetsPurchasedByType = new Map<string, number>();
  if (saveData.setsPurchasedByType) {
    Object.entries(saveData.setsPurchasedByType).forEach(([setId, count]) => {
      newSetsPurchasedByType.set(setId, count);
    });
  }

  // Convert unlocked achievements array back to Set (with fallback for old saves)
  // Filter out any achievement IDs that don't exist in AchievementInfoTotal
  // This handles cases where achievements have been removed from the game
  const validAchievementIds = (saveData.unlockedAchievements ?? []).filter(
    (achievementId) => AchievementInfoTotal.has(achievementId)
  );
  const newUnlockedAchievements = new Set<string>(validAchievementIds);

  // Merge buy amounts from save (with fallback for old saves)
  const newBrickPackBuyAmounts = new Map<string, number>();
  if (saveData.brickPackBuyAmounts) {
    Object.entries(saveData.brickPackBuyAmounts).forEach(([packId, amount]) => {
      newBrickPackBuyAmounts.set(packId, amount);
    });
  }

  const newSetBuyAmounts = new Map<string, number>();
  if (saveData.setBuyAmounts) {
    Object.entries(saveData.setBuyAmounts).forEach(([setId, amount]) => {
      newSetBuyAmounts.set(setId, amount);
    });
  }

  return {
    ...baseState,
    bricks: newBricks,
    actions: newActions,
    brickPackHistory: newBrickPackHistory,
    brickPackStats: newBrickPackStats,
    brickPackUpgrades: newBrickPackUpgrades,
    brickPackAutoCollect: newBrickPackAutoCollect,
    brickPackBuyAmounts: newBrickPackBuyAmounts,
    setBuyAmounts: newSetBuyAmounts,
    sets: newSets,
    useActionRows: saveData.useActionRows ?? baseState.useActionRows,
    useInventoryRows: saveData.useInventoryRows ?? baseState.useInventoryRows,
    actionOrder: saveData.actionOrder ?? [], // Default to empty array (use default order)
    editModeActive: false, // Always default to false (not saved)
    inventoryOrder: saveData.inventoryOrder ?? [], // Default to empty array (use default order)
    editModeInventory: false, // Always default to false (not saved)
    helpIconAnimated: saveData.helpIconAnimated ?? true, // Default to true for old saves
    totalBricksCollected: saveData.totalBricksCollected ?? 0,
    bricksCollectedByType: newBricksCollectedByType,
    totalActionsTriggered: saveData.totalActionsTriggered ?? 0,
    actionsTriggeredByType: newActionsTriggeredByType,
    totalSetsPurchased: saveData.totalSetsPurchased ?? 0,
    setsPurchasedByType: newSetsPurchasedByType,
    unlockedAchievements: newUnlockedAchievements,
    actionTimestamps: [], // Always start fresh
  };
};

/**
 * Saves the current state to localStorage
 */
export const saveToLocalStorage = (state: State): void => {
  try {
    const saveData = serializeState(state);
    const jsonString = JSON.stringify(saveData);
    localStorage.setItem(SAVE_KEY, jsonString);
  } catch (error) {
    console.error("[SaveLoad] Failed to save to localStorage:", error);
  }
};

/**
 * Loads the save data from localStorage
 * Returns null if no save exists or if loading fails
 */
export const loadFromLocalStorage = (): SaveData | null => {
  try {
    const jsonString = localStorage.getItem(SAVE_KEY);
    if (!jsonString) {
      return null;
    }

    const saveData = JSON.parse(jsonString) as SaveData;
    return saveData;
  } catch (error) {
    console.error("[SaveLoad] Failed to load from localStorage:", error);
    return null;
  }
};

/**
 * Clears the save data from localStorage
 */
export const clearLocalStorage = (): void => {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch (error) {
    console.error("[SaveLoad] Failed to clear localStorage:", error);
  }
};

/**
 * Downloads the current state as a JSON file
 */
export const downloadSaveFile = (
  state: State,
  filename: string = "brickpacked_save.json"
): void => {
  try {
    const saveData = serializeState(state);
    const jsonString = JSON.stringify(saveData, null, 2); // Pretty print with 2 spaces

    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("[SaveLoad] Failed to download save file:", error);
  }
};

/**
 * Uploads a save file and returns the parsed SaveData
 * Returns a promise that resolves with the SaveData or null if parsing fails
 */
export const uploadSaveFile = (file: File): Promise<SaveData | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const jsonString = event.target?.result as string;
        const saveData = JSON.parse(jsonString) as SaveData;
        resolve(saveData);
      } catch (error) {
        console.error("[SaveLoad] Failed to parse save file:", error);
        resolve(null);
      }
    };

    reader.onerror = () => {
      console.error("[SaveLoad] Failed to read save file");
      resolve(null);
    };

    reader.readAsText(file);
  });
};

/**
 * Validates that save data has the minimum required structure
 * Returns an error message if validation fails, or null if valid
 */
export const validateSaveData = (data: any): string | null => {
  if (typeof data !== "object" || data === null) {
    return "Save file is not a valid JSON object.";
  }

  // Check for required fields
  const missingFields: string[] = [];

  if (!("version" in data) || typeof data.version !== "string") {
    missingFields.push("version");
  }

  if (!("timestamp" in data) || typeof data.timestamp !== "number") {
    missingFields.push("timestamp");
  }

  if (!("bricks" in data) || typeof data.bricks !== "object") {
    missingFields.push("bricks");
  }

  if (missingFields.length > 0) {
    return `Save file is missing required fields: ${missingFields.join(", ")}`;
  }

  return null; // Valid save data
};
